import { useEffect, useState } from 'react';
import {
  UsersIcon, BookOpenIcon, MusicalNoteIcon, DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { getTotalUserCount, getTotalOviCount, getTotalChapterCount, getUsers } from '@/services/adminService';
import type { AppUser } from '@dnyaneshwari/shared';
import { formatRelativeTime } from '@/utils/dates';

export default function DashboardPage() {
  const [stats, setStats] = useState({ users: 0, ovis: 0, chapters: 0 });
  const [recentUsers, setRecentUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [users, ovis, chapters, { users: recent }] = await Promise.all([
          getTotalUserCount(),
          getTotalOviCount(),
          getTotalChapterCount(),
          getUsers(5),
        ]);
        setStats({ users, ovis, chapters });
        setRecentUsers(recent);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const cards = [
    { label: 'Total Users', value: stats.users, icon: UsersIcon, color: 'bg-blue-50 text-blue-600' },
    { label: 'Chapters in Firestore', value: stats.chapters, icon: DocumentTextIcon, color: 'bg-amber-50 text-amber-600' },
    { label: 'Ovis Imported', value: stats.ovis.toLocaleString(), icon: BookOpenIcon, color: 'bg-green-50 text-green-600' },
    { label: 'Total / 9032', value: `${Math.round((stats.ovis / 9032) * 100)}%`, icon: MusicalNoteIcon, color: 'bg-purple-50 text-purple-600', sub: 'Import progress' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Dnyaneshwari App — Overview</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(({ label, value, icon: Icon, color, sub }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-500">{label}</span>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
            </div>
            {loading ? (
              <div className="h-8 w-20 bg-gray-100 rounded animate-pulse" />
            ) : (
              <p className="text-3xl font-bold text-gray-900">{value}</p>
            )}
            {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Recent Users</h3>
          </div>
          {loading ? (
            <div className="p-5 space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : recentUsers.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              No users yet. Share the app to get started.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                    {(u.displayName || u.phoneNumber || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {u.displayName || u.phoneNumber || 'Anonymous'}
                    </p>
                    <p className="text-xs text-gray-400">{u.platform} · {u.language}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {formatRelativeTime(u.lastActiveAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Content Status */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Content Import Status</h3>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Ovis imported</span>
              <span className="text-sm font-semibold text-gray-900">{stats.ovis.toLocaleString()} / 9,032</span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-6">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all"
                style={{ width: `${Math.min((stats.ovis / 9032) * 100, 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Chapters seeded</span>
              <span className="text-sm font-semibold text-gray-900">{stats.chapters} / 18</span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all"
                style={{ width: `${Math.min((stats.chapters / 18) * 100, 100)}%` }}
              />
            </div>
            <div className="mt-6 text-center">
              <a
                href="/content"
                className="text-sm text-amber-600 font-medium hover:underline"
              >
                Go to Content Manager →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
